import React from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import MainNavigation from '../components/MainNavigation';
import Footer from '../components/Footer';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';
import { 
  DollarSign, 
  Upload, 
  Globe, 
  Shield, 
  TrendingUp, 
  Users,
  CheckCircle,
  HelpCircle,
  ChevronDown
} from 'lucide-react';

const DesignerInfoPage = () => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [openFaq, setOpenFaq] = React.useState(null);

  const handleNavigate = (page) => {
    if (page === 'home') navigate('/');
    else if (page === 'services') navigate('/services');
    else if (page === 'marketplace') navigate('/marketplace');
    else if (page === 'about') navigate('/about');
    else if (page === 'contact') navigate('/contact');
    else if (page === 'cart') navigate('/cart');
    else if (page === 'profile') navigate('/profile');
  };

  const benefits = [
    {
      icon: <DollarSign className="w-6 h-6" />,
      title: "Earn Revenue",
      description: "Generate income from your existing designs or create new ones. You set the price and offer models completely free or set a license cost. You get to keep 90% of the license costs you list with only 10% going to cover transaction and hosting fees."
    },
    {
      icon: <Globe className="w-6 h-6" />,
      title: "Global Reach",
      description: "Your designs are showcased to customers worldwide. Our platform handles marketing, payments, and customer support so you can focus on creating."
    },
    {
      icon: <Upload className="w-6 h-6" />,
      title: "Easy Upload Process",
      description: "Our streamlined upload process makes it simple to list your designs. Support for STEP, STL, 3MF and other popular CAD formats."
    },
    {
      icon: <Shield className="w-6 h-6" />,
      title: "Protected Designs",
      description: "You maintain full ownership of your intellectual property, offering use licenses only for either hobbyist or commercial use and you get to set your own license costs."
    },
    {
      icon: <TrendingUp className="w-6 h-6" />,
      title: "Analytics Dashboard",
      description: "Track your performance with detailed analytics. See views, downloads, and earnings all in one place on your Designer Dashboard."
    },
    {
      icon: <Users className="w-6 h-6" />,
      title: "Growing Community",
      description: "Join a community of talented designers. Get featured on our platform and build your reputation in the 3D printing space."
    }
  ];

  const faqs = [
    {
      question: "Who can become a designer?",
      answer: "Anyone with original 3D CAD designs can apply to become a designer. We welcome hobbyists, professionals, engineers, and artists. You'll need to submit a brief application with examples of your work for review."
    },
    {
      question: "What file formats are accepted?",
      answer: "We accept STEP (.step, .stp), STL (.stl), and 3MF (.3mf) files. STEP files are preferred as they preserve the original CAD geometry and allow for better print quality analysis."
    },
    {
      question: "How much can I earn?",
      answer: "You earn 90% of the license cost of each digital download sale. You set your own prices for personal and commercial licenses. There's no limit to how much you can earn."
    },
    {
      question: "How do I get paid?",
      answer: "Earnings are tracked in real-time on your Designer Dashboard. Payouts are processed monthly via your preferred payment method once you reach the minimum threshold."
    },
    {
      question: "Can I sell designs I've already created?",
      answer: "Yes! You can upload and sell any original designs you own the rights to. This is a great way to generate passive income from designs you've already created."
    },
    {
      question: "What happens when someone orders a print?",
      answer: "When a customer orders a print of your design, we handle everything - printing, quality control, packaging, and shipping. You earn your commission automatically with no extra work required."
    },
    {
      question: "How long does the application review take?",
      answer: "We review applications within 2-3 business days. Once approved, you can immediately start uploading designs to your storefront."
    },
    {
      question: "Can I remove or update my designs later?",
      answer: "Absolutely. You have full control over your listings. You can update prices, descriptions, images, or remove designs entirely at any time from your Designer Dashboard."
    },
    {
      question: "Can I list my designs with you if they are listed on other websites?",
      answer: "Yes! We require no exclusivity."
    }
  ];

  return (
    <div className="min-h-screen bg-white">
      <MainNavigation onNavigate={handleNavigate} currentPage="marketplace" />
      
      {/* Hero Section - Enhanced Swiss Tech Style */}
      <div className="relative bg-[#0007b5] text-white overflow-hidden">
        {/* Subtle noise texture overlay */}
        <div className="absolute inset-0 opacity-[0.03]" style={{
          backgroundImage: `url("data:image/svg+xml,%3Csvg viewBox='0 0 200 200' xmlns='http://www.w3.org/2000/svg'%3E%3Cfilter id='noiseFilter'%3E%3CfeTurbulence type='fractalNoise' baseFrequency='0.65' numOctaves='3' stitchTiles='stitch'/%3E%3C/filter%3E%3Crect width='100%25' height='100%25' filter='url(%23noiseFilter)'/%3E%3C/svg%3E")`
        }}></div>
        
        {/* Decorative elements */}
        <div className="absolute inset-0 overflow-hidden">
          <div className="absolute top-0 left-1/4 w-[500px] h-[500px] bg-white/5 rounded-full blur-3xl transform -translate-y-1/2"></div>
          <div className="absolute bottom-0 right-1/4 w-[400px] h-[400px] bg-blue-400/10 rounded-full blur-3xl transform translate-y-1/2"></div>
        </div>
        
        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-24 md:py-32">
          <div className="text-center max-w-4xl mx-auto">
            {/* Caption */}
            <p className="text-blue-200 text-sm font-medium tracking-widest uppercase mb-6">Designer Program</p>
            
            <h1 className="text-4xl md:text-5xl lg:text-6xl font-bold mb-8 tracking-tight font-heading">
              Join Create 3D Works as a Designer
            </h1>
            <p className="text-xl text-blue-100 mb-8 leading-relaxed">
              Turn your 3D designs into a source of income. Join our growing community of designers and reach customers worldwide.
            </p>
            
            {user?.is_designer ? (
              <Button 
                onClick={() => navigate('/designer/dashboard')}
                className="bg-white text-[#0007b5] hover:bg-blue-50 px-10 py-4 text-lg font-semibold rounded-xl shadow-lg shadow-black/10 hover:-translate-y-0.5 transition-all duration-300"
              >
                Go to Designer Dashboard
              </Button>
            ) : (
              <Button 
                onClick={() => navigate('/designer/apply')}
                className="bg-white text-[#0007b5] hover:bg-blue-50 px-10 py-4 text-lg font-semibold rounded-xl shadow-lg shadow-black/10 hover:-translate-y-0.5 transition-all duration-300"
              >
                Apply to Join as a Designer
              </Button>
            )}
          </div>
        </div>
      </div>

      {/* Benefits Section - Enhanced */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-20 md:py-32">
        <div className="text-center mb-16">
          <p className="text-[#0007b5] text-sm font-medium tracking-widest uppercase mb-4">Benefits</p>
          <h2 className="text-3xl md:text-4xl lg:text-5xl font-bold text-slate-900 mb-6 tracking-tight font-heading">
            Why Join as a Designer?
          </h2>
          <p className="text-lg text-slate-600 max-w-2xl mx-auto leading-relaxed">
            We provide everything you need to monetize your designs and grow your brand.
          </p>
        </div>
        
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
          {benefits.map((benefit, index) => (
            <Card key={index} className="border-slate-100 hover:shadow-xl hover:-translate-y-1 transition-all duration-300 rounded-2xl overflow-hidden group">
              <CardContent className="p-8">
                <div className="w-14 h-14 bg-blue-50 group-hover:bg-blue-100 rounded-2xl flex items-center justify-center text-[#0007b5] mb-6 transition-colors">
                  {benefit.icon}
                </div>
                <h3 className="text-xl font-bold text-slate-900 mb-3">{benefit.title}</h3>
                <p className="text-slate-600 leading-relaxed">{benefit.description}</p>
              </CardContent>
            </Card>
          ))}
        </div>
      </div>

      {/* How It Works Section */}
      <div className="bg-slate-50 py-16 md:py-24">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <p className="text-[#0007b5] text-sm font-medium tracking-widest uppercase mb-4">Process</p>
            <h2 className="text-3xl md:text-4xl lg:text-5xl font-bold text-slate-900 mb-6 tracking-tight font-heading">
              How It Works
            </h2>
            <p className="text-lg text-slate-600 max-w-2xl mx-auto leading-relaxed">
              Getting started as a designer is simple and straightforward.
            </p>
          </div>
          
          <div className="grid grid-cols-1 md:grid-cols-4 gap-10">
            {[
              { step: "1", title: "Apply", description: "Submit your application with examples of your work" },
              { step: "2", title: "Get Approved", description: "Our team reviews applications within 2-3 business days" },
              { step: "3", title: "Upload Designs", description: "List your designs with pricing and descriptions" },
              { step: "4", title: "Earn Money", description: "Get paid when customers download or print your designs" }
            ].map((item, index) => (
              <div key={index} className="text-center group">
                <div className="w-20 h-20 bg-[#0007b5] text-white rounded-2xl flex items-center justify-center text-3xl font-bold mx-auto mb-6 shadow-lg shadow-blue-900/20 group-hover:shadow-xl group-hover:-translate-y-1 transition-all duration-300">
                  {item.step}
                </div>
                <h3 className="text-xl font-bold text-slate-900 mb-3">{item.title}</h3>
                <p className="text-slate-600 leading-relaxed">{item.description}</p>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* FAQ Section - Enhanced */}
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-20 md:py-32">
        <div className="text-center mb-16">
          <p className="text-[#0007b5] text-sm font-medium tracking-widest uppercase mb-4">FAQ</p>
          <h2 className="text-3xl md:text-4xl lg:text-5xl font-bold text-slate-900 mb-6 tracking-tight font-heading">
            Frequently Asked Questions
          </h2>
          <p className="text-lg text-slate-600 leading-relaxed">
            Everything you need to know about joining as a designer.
          </p>
        </div>
        
        <div className="space-y-4">
          {faqs.map((faq, index) => (
            <div 
              key={index} 
              className="border border-slate-200 rounded-2xl overflow-hidden bg-white hover:border-slate-300 transition-colors"
            >
              <button
                onClick={() => setOpenFaq(openFaq === index ? null : index)}
                className="w-full px-6 py-5 text-left flex items-center justify-between hover:bg-slate-50 transition-colors"
              >
                <span className="font-semibold text-slate-900 flex items-center">
                  <HelpCircle className="w-5 h-5 text-[#0007b5] mr-4 flex-shrink-0" />
                  {faq.question}
                </span>
                <ChevronDown 
                  className={`w-5 h-5 text-slate-400 transition-transform duration-300 ${
                    openFaq === index ? 'rotate-180' : ''
                  }`} 
                />
              </button>
              {openFaq === index && (
                <div className="px-6 pb-5 pt-0">
                  <p className="text-slate-600 pl-9 leading-relaxed">{faq.answer}</p>
                </div>
              )}
            </div>
          ))}
        </div>
      </div>

      {/* CTA Section - Enhanced */}
      <div className="bg-[#0007b5] text-white py-20 md:py-28 relative overflow-hidden">
        {/* Decorative elements */}
        <div className="absolute inset-0 overflow-hidden">
          <div className="absolute top-0 right-0 w-[400px] h-[400px] bg-white/5 rounded-full blur-3xl transform translate-x-1/2 -translate-y-1/2"></div>
          <div className="absolute bottom-0 left-0 w-[300px] h-[300px] bg-blue-400/10 rounded-full blur-3xl transform -translate-x-1/2 translate-y-1/2"></div>
        </div>
        
        <div className="relative max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-3xl md:text-4xl lg:text-5xl font-bold mb-6 tracking-tight font-heading">
            Ready to Start Earning?
          </h2>
          <p className="text-xl text-blue-100 mb-10 leading-relaxed max-w-2xl mx-auto">
            Join our community of designers and turn your creativity into income.
          </p>
          
          {user?.is_designer ? (
            <Button 
              onClick={() => navigate('/designer/dashboard')}
              className="bg-white text-[#0007b5] hover:bg-blue-50 px-10 py-4 text-lg font-semibold rounded-xl shadow-lg shadow-black/10 hover:-translate-y-0.5 transition-all duration-300"
            >
              Go to Designer Dashboard
            </Button>
          ) : (
            <Button 
              onClick={() => navigate('/designer/apply')}
              className="bg-white text-[#0007b5] hover:bg-blue-50 px-10 py-4 text-lg font-semibold rounded-xl shadow-lg shadow-black/10 hover:-translate-y-0.5 transition-all duration-300"
            >
              Apply to Join as a Designer
            </Button>
          )}
        </div>
      </div>

      <Footer />
    </div>
  );
};

export default DesignerInfoPage;
